// What the internal customer edit form saves, decided before anything is sent.
// The form holds the whole record; a save sends only the fields that differ from the
// record as loaded, with the reason and the version it was edited against. Every
// outcome other than a request carries a message for the user: Save never does
// nothing without saying why.
// Pure functions (no React), so tests run them under node.

// Keys of the record that are not fields a save changes (the key and the version).
const NOT_FIELDS = new Set(['customer_id', 'updated_at'])

// Absent, null and the empty string all mean "no value": clearing a box the record
// never had a value for is not a change.
function canonical(v) {
  if (v === undefined || v === null || v === '') return null
  if (Array.isArray(v)) return v.map(canonical)
  if (typeof v === 'object') {
    const out = {}
    for (const k of Object.keys(v).sort()) {
      const c = canonical(v[k])
      if (c !== null) out[k] = c
    }
    return out
  }
  return v
}

export function sameValue(a, b) {
  return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b))
}

// The record's fields whose value in `form` differs from `original`, sorted. Every key
// of either object counts, so no field can be left out of the comparison. Terminology
// is edited in its own state; pass both sides of it to compare it too.
export function changedFields(original, form, { terminology, originalTerminology } = {}) {
  const base = original || {}
  const now = form || {}
  const keys = new Set([...Object.keys(base), ...Object.keys(now)])
  const out = []
  for (const k of keys) {
    if (NOT_FIELDS.has(k) || k === 'terminology') continue
    if (!sameValue(base[k], now[k])) out.push(k)
  }
  const termBefore = originalTerminology !== undefined ? originalTerminology : base.terminology
  const termNow = terminology !== undefined ? terminology : now.terminology
  if (!sameValue(termBefore, termNow)) out.push('terminology')
  return out.sort()
}

export const NOTHING_CHANGED = 'Nothing to save: no field has changed since the record was loaded or last saved.'
export const REASON_NEEDED = 'Say why you are making this change in the box at the top, next to Save Changes. It goes in the history.'
export const SERVER_NO_CHANGE = 'Nothing was saved: the server found no difference from the stored record.'

// What pressing Save does: { send: true, payload, fields } or { send: false, kind, message }.
export function savePlan({ original, form, terminology, originalTerminology, reason }) {
  const fields = changedFields(original, form, { terminology, originalTerminology })
  if (fields.length === 0) return { send: false, kind: 'unchanged', message: NOTHING_CHANGED, fields }
  const why = String(reason || '').trim()
  if (!why) return { send: false, kind: 'reason', message: REASON_NEEDED, fields }
  const payload = {}
  for (const f of fields) {
    const v = f === 'terminology' && terminology !== undefined ? terminology : form[f]
    // A box emptied by the user ('' or null) is a clear, and the server clears on null.
    payload[f] = canonical(v) === null ? null : v
  }
  payload.reason = why
  payload.expected_updated_at = original?.updated_at || null
  return { send: true, payload, fields }
}

// After a 200: the server returns the stored record unchanged (same version) when it
// found nothing to write, so a "Saved" there would be false.
export function serverRecordedChange(before, after) {
  return Boolean(after) && (after.updated_at || null) !== (before?.updated_at || null)
}
